<template>
  <div class="rmaForm">
    <div>
      <p>
        <span v-html="$mlGet('rmaConfirm2-header')"></span>
      </p>
      <p>
        <span v-html="getText"></span>
      </p>
    </div>
    <!-- new confirmation button -->
    <div>
      <Checkbox
        id="contact-directly-input"
        style="grid-column: 1;"
        :option1Text="$mlGet('rmaConfirmation-userconfirm-checkbox')"
        v-model="radioState"
        :confirmOnly="true"
      />
    </div>
    <div class="information-box">
      <span
        v-if="rmaReady"
        v-html="$mlGet('rmaConfirm2-informationBox')"
      ></span>
      <span v-else v-html="$mlGet('rmaConfirm2-not-confirmed')"></span>
    </div>
    <button @click="saveAndProcessRma" :disabled="!rmaReady">
      {{ $mlGet("rmaConfirm2-button-saveAndProcess") }}
    </button>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";

import Rma from "@/store/RmaModel";
import { PageChangeContext, PageName } from "@/views/CreateRma.vue";
import Checkbox from "../Input/Checkbox.vue";
import { renderString } from "nunjucks";

import * as Sentry from "@sentry/vue";

interface SelectedReason {
  id: number;
  name: string;
  showFields: boolean;
  needFields: boolean;
  needPriceQuotation: boolean;
}

interface TransportOffer {
  id: string;
  alerts: Alert[];
  service: string;
  currency: string;
  totalCharges: string;
  warranty: boolean;
  transportPossible: boolean;
  fieldServicePossible: boolean;
  price: number;
  isDealerProvided: boolean;
  couponTransport: boolean;
  includesTax: boolean;
}

interface Alert {
  Code: string;
  Description: string;
}

@Component({
  components: {
    Checkbox
  }
})
export default class RmaConfirm2 extends Vue {
  public radioState = false;

  public loading = true;

  public selectedReason: SelectedReason | null = null;
  public rmaTransportOffer: TransportOffer | null = null;

  async getTransportPrices(): Promise<void> {
    const response = await this.$axios.get(
      `/rma/byId/${this.$store.state.rma.rmaId}`
    );
    this.rmaTransportOffer = response.data.rmaTransportOffer
      ? (JSON.parse(response.data.rmaTransportOffer) as TransportOffer)
      : null;
  }

  async getRmaReason(): Promise<void> {
    const response = await this.$axios.get(
      `/rma/reasons/${this.$store.state.rma.model.fmId}`
    );

    const reason = response.data.find(
      (reason: any) => reason.id == this.$store.state.rma.reasonForReturn
    );

    this.selectedReason = {
      id: reason.id,
      name: reason.languageItemKey,
      showFields: reason.showFields,
      needFields: reason.needFields,
      needPriceQuotation: reason.needPriceQuotation
    };
  }

  async mounted() {
    await this.getTransportPrices();
    await this.getRmaReason();
    this.loading = false;
  }

  get getText() {
    if (this.loading) {
      return "...";
    }

    return renderString(this.$mlGet("rmaConfirmation-text"), {
      transportOffer: {
        ...this.rmaTransportOffer,
        price: this.rmaTransportOffer?.price.toFixed(2)
      },
      selectedReason: this.selectedReason,
      rma: {
        warranty: !this.selectedReason?.needPriceQuotation,
        pickup: this.$store.state.rma.transportUpsPickup,
        hanexShipping: this.$store.state.rma.transportHanexShipping,
        modelName: this.$store.state.rma.model.name,
        makeName: this.$store.state.rma.model.make.name,
        priceQuotation: this.$store.state.rma.priceQuotation,
        pickupDate: new Date(
          this.$store.state.rma.upsPickupPreveredDate
        ).toLocaleDateString()
      },
      user: {
        isEnterprise: this.$store.state.user.isEnterprise
      }
    });
  }

  async axiosRequest(
    lazyRma: Omit<Rma, "model"> & { model: { fmId: number } }
  ): Promise<void> {
    await this.$axios.put(
      `/rma/save/${
        this.$store.state.rma.rmaId ? this.$store.state.rma.rmaId : ""
      }`,
      lazyRma
    );
  }

  async saveAndProcessRma(): Promise<void> {
    this.$swal({
      title: this.$mlGet("RmaConfirm2-alert2-title"),
      text: this.$mlGet("RmaConfirm2-alert2-text"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$mlGet("RmaConfirm2-alert2-confirm"),
      cancelButtonText: this.$mlGet("RmaConfirm2-alert2-cancel"),
      reverseButtons: true
    }).then(async result => {
      if (result.isConfirmed == true) {
        // Write the rma confirm state. This property is only saved when we process the rma. (Needs to be confirmed every time the user gets to the confirmation page.)
        this.$store.state.rma.rmaConfirmationState = this.radioState ? 1 : 0;

        const lazyRma: Omit<Rma, "model"> & {
          model: { fmId: number };
        } = Object.assign({}, this.$store.state.rma);
        lazyRma.model = { fmId: this.$store.state.rma.model.fmId };

        await this.axiosRequest(lazyRma)
          .then(async () => {
            await this.$axios
              .put(`/rmas/process/`, {
                rmaIds: [lazyRma.rmaId]
              })
              .then(async () => {
                Sentry.metrics.count("rma_confirmed", 1);
                this.$swal({
                  title: this.$mlGet("RmaConfirm2-alert2-confirmed-title"),
                  text: this.$mlGet("RmaConfirm2-alert2-confirmed-text"),
                  icon: "success"
                });
                await this.$store.dispatch("downloadUserRmas");
                this.$store.commit("cleanRma");
                this.$changePage("ModelSelection", {
                  skipPageExit: true,
                  saveRma: false,
                  clearHistory: true
                });
              })
              .catch(err => {
                this.$swal({
                  title: this.$mlGet("RmaConfirm2-alert2-error-title"),
                  text: err.response.data.error,
                  icon: "error"
                });
              });
          })
          .catch(err => {
            this.$swal({
              title: this.$mlGet("RmaConfirm2-alert2-error-title"),
              text: err.response.data.error,
              icon: "error"
            });
          });
      }
    });
  }

  public get rmaReady(): boolean {
    return this.radioState;
  }

  public async beforePageLeave(context: PageChangeContext): Promise<boolean> {
    return true;
  }

  public async afterPageEnter(context: PageChangeContext): Promise<void> {
    return;
  }

  public async onPageLeave(context: PageChangeContext): Promise<PageName> {
    return "RmaConfirmation";
  }
}
</script>

<style scoped>
* {
  text-align: left;
}

.labelResult {
  margin: 5px 2ex;
}

.label {
  margin: 0 20px 0 0;
}

.formRadioQuestion {
  display: block;
  padding-bottom: 2ex;
}

button {
  background-color: #da6f38;
  padding: 8px 16px;
  font-size: 120%;
  color: white;
  margin: 10px;
  cursor: pointer;
  border: none;
  float: right;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

#rmaClientReference-wrapper {
  padding: 2ex 0;
}

#input-clientReference {
  border: 1px solid lightgray;
  box-sizing: border-box;
  padding: 5px;
  margin: 5px 0;
  width: 100%;
}
</style>
